import { useMemo } from 'react'
import {
  useAssetListQuery,
  useSapOrdersQuery,
  useOrderRecipeQuery,
  useOrderPeriodsQuery,
  usePeriodConsumptionsQuery,
  useConsumptionsFallbackQuery,
  useHopperPeriodsQuery,
  useDosingByLineQuery,
  normalizeLineContext,
  normalizeOrderNumber,
  normalizePeriodID,
} from './useMaterialsConsumptionQueries'

/**
 * Hook compuesto que centraliza toda la carga de datos server-state
 * para `MaterialesConsumos.jsx`.
 *
 * Recibe los valores de UI-state (selectedLine, selectedOrder, activePeriod,
 * selectedFactory, fromSAP flags) y devuelve todos los datos ya normalizados
 * junto con los estados de carga/error de cada query.
 *
 * USO:
 *   const data = useMaterialsConsumptionData({
 *     selectedLine,
 *     selectedOrder,
 *     activePeriod,
 *     selectedFactory,
 *     fromSAPOrders,   // boolean, defecto false
 *     fromSAPRecipe,   // boolean, defecto false
 *   })
 *
 * Los componentes/handlers que hoy llaman loadLines/loadOrders/... pasarán a
 * leer directamente de data.lines, data.orders, data.recipe, etc.
 * Para forzar un refresco se usa data.refetchXxx() o invalidateQueries().
 */
export function useMaterialsConsumptionData({
  selectedLine = null,
  selectedOrder = null,
  activePeriod = null,
  selectedFactory = null,
  fromSAPOrders = false,
  fromSAPRecipe = false,
} = {}) {
  // -------------------------------------------------------------------------
  // Normalización de contexto
  // -------------------------------------------------------------------------
  const lineCtx = useMemo(
    () => normalizeLineContext(selectedLine, selectedFactory),
    [selectedLine, selectedFactory]
  )

  const orderNumber = useMemo(
    () => normalizeOrderNumber(selectedOrder),
    [selectedOrder]
  )

  const periodID = useMemo(
    () => normalizePeriodID(activePeriod),
    [activePeriod]
  )

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  // 1) Activos / líneas (siempre activa)
  const assetListQuery = useAssetListQuery()

  // 2) Órdenes para la línea seleccionada
  const sapOrdersQuery = useSapOrdersQuery(lineCtx, fromSAPOrders)

  // 3) Receta de la orden seleccionada
  const orderRecipeQuery = useOrderRecipeQuery(lineCtx, orderNumber, fromSAPRecipe)

  // 4) Períodos de la orden seleccionada
  const orderPeriodsQuery = useOrderPeriodsQuery(lineCtx, orderNumber)

  // 5a) Consumos del período activo (prioridad)
  const periodConsumptionsQuery = usePeriodConsumptionsQuery(periodID)

  // 5b) Consumos fallback cuando no hay período activo
  const consumptionsFallbackQuery = useConsumptionsFallbackQuery(
    lineCtx,
    orderNumber,
    !!periodID
  )

  // 6) Consumos agrupados por hopper (para Cant. Declarada en receta)
  const hopperPeriodsQuery = useHopperPeriodsQuery(lineCtx, orderNumber)

  // 7) Dosificadores y hoppers de la línea+orden actual
  const dosingByLineQuery = useDosingByLineQuery(lineCtx, orderNumber)

  // -------------------------------------------------------------------------
  // Datos derivados — lógica que hoy vive inline en el componente
  // -------------------------------------------------------------------------

  // Líneas: el filtrado por fábrica se sigue haciendo en cliente (igual que ahora)
  const rawAssets = assetListQuery.data ?? []

  // Órdenes
  const orders = sapOrdersQuery.data ?? []

  // Receta
  const recipe = orderRecipeQuery.data ?? null

  // Períodos
  const periods = orderPeriodsQuery.data ?? []

  // Período activo: priorizar IsActive, si no usar el último período
  const derivedActivePeriod = useMemo(() => {
    if (periods.length === 0) return null
    return (
      periods.find((p) => p.IsActive === true || p.isActive === true || p.is_active === true) ??
      periods[periods.length - 1]
    )
  }, [periods])

  // Consumos: períodos activo → fallback
  const consumptions = useMemo(() => {
    if (periodID) return periodConsumptionsQuery.data ?? []
    return consumptionsFallbackQuery.data ?? []
  }, [periodID, periodConsumptionsQuery.data, consumptionsFallbackQuery.data])

  // Cantidades declaradas por componente (para columna "Cant. Declarada" en receta)
  const declaredQuantitiesByComponent = useMemo(() => {
    const hopperPeriods = hopperPeriodsQuery.data ?? []
    const byComponent = {}
    hopperPeriods.forEach((period) => {
      const list = period.Consumptions || period.consumptions || []
      list.forEach((c) => {
        const code = (c.ComponentSapCode || c.componentSapCode || '').trim().toUpperCase()
        if (!code) return
        const qty =
          parseFloat(
            c.SentToSAPQuantity ??
            c.sentToSAPQuantity ??
            c.AdjustedQuantity ??
            c.adjustedQuantity ??
            0
          ) || 0
        byComponent[code] = (byComponent[code] || 0) + qty
      })
    })
    return byComponent
  }, [hopperPeriodsQuery.data])

  // Dosificadores y hoppers
  const rawDosingByLine = dosingByLineQuery.data ?? []

  // -------------------------------------------------------------------------
  // Estados de carga agregados (para reemplazar los flags individuales)
  // -------------------------------------------------------------------------

  const isLoadingLines = assetListQuery.isLoading
  const isLoadingOrders = sapOrdersQuery.isFetching
  const isLoadingRecipe = orderRecipeQuery.isFetching
  const isLoadingPeriods = orderPeriodsQuery.isFetching
  const isLoadingConsumptions = periodID
    ? periodConsumptionsQuery.isFetching
    : consumptionsFallbackQuery.isFetching
  const isLoadingDosifiers = dosingByLineQuery.isFetching

  // -------------------------------------------------------------------------
  // Contexto normalizado expuesto para uso en mutations/handlers
  // -------------------------------------------------------------------------

  const ctx = useMemo(
    () => ({
      factory: lineCtx?.factory ?? '',
      prodLine: lineCtx?.prodLine ?? '',
      sapCode: lineCtx?.sapCode ?? '',
      prodLineId: lineCtx?.prodLineId ?? '',
      orderNumber,
      periodID,
    }),
    [lineCtx, orderNumber, periodID]
  )

  // -------------------------------------------------------------------------
  // Exponer refetch de cada query (para migración incremental)
  // -------------------------------------------------------------------------

  return {
    // Contexto normalizado
    ctx,

    // Datos
    rawAssets,
    orders,
    recipe,
    periods,
    derivedActivePeriod,
    consumptions,
    declaredQuantitiesByComponent,
    rawDosingByLine,

    // Estados de carga
    isLoadingLines,
    isLoadingOrders,
    isLoadingRecipe,
    isLoadingPeriods,
    isLoadingConsumptions,
    isLoadingDosifiers,

    // Errores por query
    ordersError: sapOrdersQuery.error,
    recipeError: orderRecipeQuery.error,
    periodsError: orderPeriodsQuery.error,
    consumptionsError: periodID
      ? periodConsumptionsQuery.error
      : consumptionsFallbackQuery.error,

    // Refetch manual (para casos que aún necesiten forzar recarga explícita)
    refetchAssets: assetListQuery.refetch,
    refetchOrders: sapOrdersQuery.refetch,
    refetchRecipe: orderRecipeQuery.refetch,
    refetchPeriods: orderPeriodsQuery.refetch,
    refetchConsumptions: periodID
      ? periodConsumptionsQuery.refetch
      : consumptionsFallbackQuery.refetch,
    refetchHopperPeriods: hopperPeriodsQuery.refetch,
    refetchDosingByLine: dosingByLineQuery.refetch,

    // Queries crudas (para acceso a isFetching, isSuccess, etc. si se necesita)
    _queries: {
      assetListQuery,
      sapOrdersQuery,
      orderRecipeQuery,
      orderPeriodsQuery,
      periodConsumptionsQuery,
      consumptionsFallbackQuery,
      hopperPeriodsQuery,
      dosingByLineQuery,
    },
  }
}
